// 实时通道：一条 /ws 连接（带 token），断线指数退避重连；内网/外网切换或换账号时重连；扫码画面按任务订阅
import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { lan, wsUrl } from "./net";
import type { WsMessage } from "./types";

type Handler = (m: WsMessage) => void;

interface RealtimeApi {
  connected: boolean;
  /** 订阅所有消息，返回取消函数 */
  listen: (h: Handler) => () => void;
  /** 订阅某任务的扫码画面帧（引用计数） */
  subscribeScan: (taskId: string) => () => void;
}

const Ctx = createContext<RealtimeApi | null>(null);

export function RealtimeProvider({ token, children }: { token: string | null; children: ReactNode }) {
  const [connected, setConnected] = useState(false);
  const handlers = useRef(new Set<Handler>());
  const scanSubs = useRef(new Map<string, number>());
  const wsRef = useRef<WebSocket | null>(null);
  const [netVersion, setNetVersion] = useState(0);

  useEffect(() => lan.subscribe(() => setNetVersion((v) => v + 1)), []);

  useEffect(() => {
    if (!token || typeof WebSocket === "undefined") return;
    let stopped = false;
    let retry = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const connect = () => {
      if (stopped) return;
      const ws = new WebSocket(wsUrl(`/ws?token=${encodeURIComponent(token)}`));
      wsRef.current = ws;
      ws.onopen = () => {
        retry = 0;
        setConnected(true);
        for (const id of scanSubs.current.keys()) ws.send(JSON.stringify({ sub: id }));
      };
      ws.onmessage = (ev) => {
        let m: WsMessage;
        try {
          m = JSON.parse(String(ev.data)) as WsMessage;
        } catch {
          return;
        }
        for (const h of [...handlers.current]) {
          try {
            h(m);
          } catch (e) {
            console.error("[ws] handler", e);
          }
        }
      };
      ws.onclose = () => {
        setConnected(false);
        if (wsRef.current === ws) wsRef.current = null;
        if (stopped) return;
        const delay = Math.min(15_000, 1000 * 2 ** retry++);
        timer = setTimeout(connect, delay);
      };
      ws.onerror = () => ws.close();
    };
    connect();
    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
      wsRef.current?.close();
      wsRef.current = null;
      setConnected(false);
    };
  }, [token, netVersion]);

  const value = useMemo<RealtimeApi>(
    () => ({
      connected,
      listen: (h) => {
        handlers.current.add(h);
        return () => handlers.current.delete(h);
      },
      subscribeScan: (taskId) => {
        const n = scanSubs.current.get(taskId) ?? 0;
        scanSubs.current.set(taskId, n + 1);
        const ws = wsRef.current;
        if (n === 0 && ws?.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ sub: taskId }));
        return () => {
          const c = (scanSubs.current.get(taskId) ?? 1) - 1;
          if (c <= 0) {
            scanSubs.current.delete(taskId);
            const w = wsRef.current;
            if (w?.readyState === WebSocket.OPEN) w.send(JSON.stringify({ unsub: taskId }));
          } else scanSubs.current.set(taskId, c);
        };
      },
    }),
    [connected],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useRealtime(): RealtimeApi {
  const v = useContext(Ctx);
  if (!v) throw new Error("useRealtime 必须在 RealtimeProvider 里用");
  return v;
}

/** 监听 ws 消息（handler 用最新闭包） */
export function useWsMessages(handler: Handler): void {
  const rt = useRealtime();
  const ref = useRef(handler);
  ref.current = handler;
  useEffect(() => rt.listen((m) => ref.current(m)), [rt]);
}
